import { selectedFeatureAtom } from '@/atoms/selection.atom';
import type { FeaturesQuery } from '@/graphql/graphql';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { parseStockpileProperties, type StockpileFeatureProperties } from '@/lib/stockpile/feature';
import { Html } from '@react-three/drei';
import { useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';

/**
 * Renders a clickable summary label at the centroid of each saved stockpile
 * polygon.
 *
 * The polygon outline + fill themselves are drawn by the existing `<GeoJson />`
 * pipeline (Viewer.tsx) — we deliberately do NOT re-draw them here to avoid
 * z-fighting and double rendering. SavedStockpiles only owns the labels and
 * the click-to-edit affordance.
 *
 * **Why drei `<Html>` and not `<Text>`** — we previously used `<Text>` (Troika
 * SDF text in WebGL) plus a `<planeGeometry>` chip. That gave grainy text at
 * shallow camera angles, world-units font sizing that's awkward to tune across
 * scenes, and a flat plane chip that visibly billboards out of position when
 * the camera is below the label. `<Html>` portals a real DOM element over the
 * canvas and tracks the centroid 2-D screen position automatically — text is
 * pixel-crisp at every zoom, hover/click events are real DOM events (no R3F
 * raycast), and styling is just CSS.
 *
 * Trade-off: HTML labels render purely as overlay; they don't depth-test
 * against scene geometry. That's actually what we want for stockpile labels —
 * they should always be visible, never occluded by the pile they describe.
 */

type Feature = FeaturesQuery['featuresByLayer'][number];

/**
 * Saved-stockpile feature plus its parsed flat properties. We pre-parse
 * once in the parent and pass the result down so the label component
 * doesn't redo the parser per render.
 */
interface StockpileLabelInput {
  feature: Feature;
  centroid: THREE.Vector3;
  props: StockpileFeatureProperties;
}

/**
 * Format a volume in m³ for display in the label.
 *
 * NOTE: previous versions used a "1000 m³ → 1 k m³" abbreviation, which is
 * ambiguous next to the "m³" suffix the call site appends — readers parse
 * "1 k m³" as "1 km³" (= 10⁹ m³). We don't abbreviate any more — just use
 * locale-appropriate thousands separators ("1,234 m³" / "1 234 m³"). Volumes
 * past ~1 M m³ are rare in stockpile work; if they come up we can revisit.
 */
const formatVolume = (v: number) => {
  if (!Number.isFinite(v)) return '—';
  if (v >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (v >= 100) return v.toFixed(0);
  if (v >= 10) return v.toFixed(1);
  return v.toFixed(2);
};

/**
 * Format a mass in kg for display. Tonnes are a real SI-coherent unit
 * (1 t = 1000 kg) so the kg→t breakpoint is fine; not the same conceptual
 * mistake as the volume "k" abbreviation.
 */
const formatMassTonnes = (kg: number) => {
  if (!Number.isFinite(kg)) return '—';
  if (kg >= 1000) return `${(kg / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })}\u202Ft`;
  return `${kg.toLocaleString(undefined, { maximumFractionDigits: 0 })}\u202Fkg`;
};

const SavedStockpileLabel = ({
  feature,
  centroid,
  props,
  onEdit,
}: {
  feature: Feature;
  centroid: THREE.Vector3;
  props: StockpileFeatureProperties;
  onEdit: (feature: Feature) => void;
}) => {
  const { t } = useTranslation('project');
  const [hovered, setHovered] = useState(false);

  const volumeText = `${formatVolume(props.volumeM3)}\u202Fm³`;
  const massText = props.massKg != null ? formatMassTonnes(props.massKg) : null;
  const nameText = props.name.length > 0 ? props.name : t('stockpile.untitled');

  return (
    <group position={centroid.toArray()}>
      <Html
        // `center` applies translate(-50%, -50%) so the chip sits centred on
        // the 3D point. No `transform` / `distanceFactor`: the label keeps a
        // fixed pixel size regardless of zoom — far easier to read than the
        // old world-units chip that scaled with camera distance.
        center
        // Stack ordering: keep stockpile labels on top of any other 3D HTML
        // overlays. drei sorts by z within this range; a wider near-bound
        // makes sure stockpile labels always win.
        zIndexRange={[200, 0]}
        // Don't intercept canvas events outside the chip itself.
        // (`pointer-events: none` on the wrapper; we re-enable on the chip.)
        style={{ pointerEvents: 'none' }}>
        <div
          role='button'
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(feature);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEdit(feature);
            }
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            // Inline styles (instead of Tailwind classes) so the look is
            // independent of any class-purging surprises in the prod build.
            // The canvas mount can also live outside the body's normal class
            // tree; this isolates the label.
            pointerEvents: 'auto',
            cursor: 'pointer',
            userSelect: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1px',
            minWidth: '110px',
            padding: '6px 12px 7px',
            borderRadius: '8px',
            border: hovered ? '1px solid rgba(251, 146, 60, 0.85)' : '1px solid rgba(251, 146, 60, 0.35)',
            background: 'rgba(15, 15, 17, 0.86)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            boxShadow: hovered
              ? '0 6px 18px rgba(0, 0, 0, 0.45), 0 0 0 3px rgba(251, 146, 60, 0.15)'
              : '0 4px 12px rgba(0, 0, 0, 0.35)',
            transition: 'border-color 120ms, box-shadow 120ms, transform 120ms',
            transform: hovered ? 'scale(1.04)' : 'scale(1)',
            color: 'white',
            font: '500 12px/1.15 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            whiteSpace: 'nowrap',
          }}>
          {/* Name — small, dimmed, tightens the visual hierarchy. */}
          <div
            style={{
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'rgba(214, 211, 209, 0.95)',
              maxWidth: '160px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={nameText}>
            {nameText}
          </div>
          {/* Volume — the headline metric. */}
          <div
            style={{
              fontSize: '17px',
              fontWeight: 700,
              color: '#fb923c',
              lineHeight: 1.1,
              letterSpacing: '-0.01em',
            }}>
            {volumeText}
          </div>
          {/* Mass + material — only when present. */}
          {massText && (
            <div
              style={{
                fontSize: '11px',
                fontWeight: 500,
                color: 'rgba(212, 212, 216, 0.9)',
                lineHeight: 1.1,
              }}>
              {massText}
            </div>
          )}
        </div>
      </Html>
    </group>
  );
};

export const SavedStockpiles = ({ features }: { features: Feature[] }) => {
  // Parse once per feature, up front. `parseStockpileProperties` returns
  // null for non-stockpile features, which doubles as the kind filter.
  const labels = useMemo(() => {
    const out: StockpileLabelInput[] = [];
    for (const f of features) {
      const props = parseStockpileProperties(f.properties);
      if (!props) continue;
      const geom = f.geometry as { type?: string; coordinates?: number[][][] } | null;
      if (!geom || geom.type !== 'Polygon' || !geom.coordinates?.[0]) continue;
      const ring = geom.coordinates[0];
      // Drop the closing duplicate vertex if present.
      const verts =
        ring.length > 1 &&
        ring[0][0] === ring[ring.length - 1][0] &&
        ring[0][1] === ring[ring.length - 1][1]
          ? ring.slice(0, -1)
          : ring;
      if (verts.length === 0) continue;
      let sx = 0;
      let sy = 0;
      let sz = 0;
      for (const v of verts) {
        sx += v[0];
        sy += v[1];
        sz += v[2] ?? 0;
      }
      const n = verts.length;
      // Bias the centroid up by the mean stockpile height so the label sits
      // above the pile rather than embedded in it. Falls back to ≥1 m so
      // flat stockpiles still get a visible separation.
      const liftedZ = sz / n + Math.max(props.meanHeightM, 1);
      out.push({ feature: f, centroid: new THREE.Vector3(sx / n, sy / n, liftedZ), props });
    }
    return out;
  }, [features]);

  const setSelectedFeature = useSetAtom(selectedFeatureAtom);
  const { createUpdateHandler } = useFeatureActions();

  /**
   * Click handler for label hits.
   *
   * Mirrors `GeoJson`'s `handleClick` selection path: write the feature
   * into `selectedFeatureAtom` (with the standard `onSave` handler) so
   * `<FeatureProperties>` opens with the saved-stockpile **results view**
   * — volume / area / mass numbers, plus a Re-compute button.
   *
   * Earlier this handler called `enterStockpileEditModeAtom` directly,
   * which jumped straight into the recompute panel. That was inconsistent
   * with clicking the polygon surface (which goes through `<GeoJson>` →
   * results view → Re-compute button), and meant users couldn't review
   * results from the label without invoking a recompute flow first.
   */
  const handleSelect = (feature: Feature) => {
    if (!feature.layerId) return;
    setSelectedFeature({
      ...feature,
      onSave: createUpdateHandler(feature.id, feature.layerId),
    });
  };

  if (labels.length === 0) return null;

  return (
    <>
      {labels.map(({ feature, centroid, props }) => (
        <SavedStockpileLabel
          key={feature.id}
          feature={feature}
          centroid={centroid}
          props={props}
          onEdit={handleSelect}
        />
      ))}
    </>
  );
};
